import {
  faAngleDoubleLeft,
  faAngleDoubleRight,
  faAngleLeft,
  faAngleRight,
  faMagnifyingGlass,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  AutoComplete,
  Button,
  Card,
  Input,
  Modal,
  Select,
  Spin,
  Table,
  Tabs,
} from "antd";
import { Option } from "antd/es/mentions";
import Title from "antd/es/typography/Title";
import { useEffect, useState } from "react";
import ReactCountryFlag from "react-country-flag";
import { useLocation, useNavigate } from "react-router-dom";
import { getDetentionColorClasses } from "../helpers/common";
import dayjs from "../helpers/dayjsSetup";
import ContainerService from "../services/Container/ContainerService";
import ShipmentService from "../services/Shipment/ShipmentService";

const ShipmentDetentionPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(
    location?.state?.activeTab || "detentionMonitoring"
  );
  const [loading, setLoading] = useState(false);
  const [selectedContainerNum, setSelectedContainerNum] = useState(
    location?.state?.selectedContainerNum || null
  );
  const [isSearchModalVisible, setIsSearchModalVisible] = useState(false);
  const [searchInput, setSearchInput] = useState(
    location?.state?.selectedContainerNum
      ? `${location?.state?.selectedContainerNum} - ${location?.state?.activeTab
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (str) => str.toUpperCase())}`
      : ""
  );
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [list, setList] = useState({
    request: {
      containerNum: location?.state?.selectedContainerNum || null,
      pageFilter: { all: false, size: 50, num: 0 },
      sortFilter: { field: "", order: "" },
    },
    response: { total: 0, data: [] },
  });
  const [counts, setCounts] = useState(0);

  const tabs = ["detentionMonitoring"];

  useEffect(() => {
    let resetRequest = {
      ...list.request,
      pageFilter: {
        ...list.request.pageFilter,
        num: 0,
      },
    };

    resetRequest = {
      ...resetRequest,
      sortFilter: { field: "", order: "" }, // Apply default sort or clear
    };

    setList((prev) => ({
      ...prev,
      request: resetRequest,
    }));
    fetchShipmentDetentionData(resetRequest);
  }, [activeTab]);

  const leftAlign = { align: "left" };
  const rightAlign = { align: "right" };
  const centerAlign = { align: "center" };

  const formatDate = (value) =>
    value ? dayjs(value).format("DD MMM YYYY") : "-";

  const dateColumn = (
    title,
    dataIndex,
    classNames = "w-28",
    defaultSortOrder = ""
  ) => ({
    key: dataIndex,
    title,
    dataIndex,
    ...leftAlign,
    render: (value) => <div className={classNames}>{formatDate(value)}</div>,
    sorter: (a, b) => new Date(a[dataIndex]) - new Date(b[dataIndex]),
    defaultSortOrder,
  });

  const leftColumn = (
    title,
    dataIndex,
    customRender,
    sortable = false,
    classNames = "w-28"
  ) => ({
    key: dataIndex,
    title,
    dataIndex,
    ...leftAlign,
    render:
      customRender ||
      ((text) => (
        <div className={classNames}>
          {typeof text === "boolean" ? (text ? "Yes" : "No") : text}
        </div>
      )),

    sorter: sortable
      ? (a, b) => {
        const valA = a[dataIndex];
        const valB = b[dataIndex];

        // Handle numbers first
        if (!isNaN(valA) && !isNaN(valB)) {
          return Number(valA) - Number(valB);
        }

        // Fallback to string comparison
        return (valA || "").toString().localeCompare((valB || "").toString());
      }
      : false,
  });

  const centerColumn = (
    title,
    dataIndex,
    customRender,
    sortable = false,
    classNames = "w-28"
  ) => ({
    key: dataIndex,
    title,
    dataIndex,
    ...centerAlign,
    render:
      customRender ||
      ((text) => (
        <div className={classNames}>
          {typeof text === "boolean" ? (text ? "Yes" : "No") : text}
        </div>
      )),

    sorter: sortable
      ? (a, b) => {
        const valA = a[dataIndex];
        const valB = b[dataIndex];

        // Handle numbers first
        if (!isNaN(valA) && !isNaN(valB)) {
          return Number(valA) - Number(valB);
        }

        // Fallback to string comparison
        return (valA || "").toString().localeCompare((valB || "").toString());
      }
      : false,
  });

  const handleView = (record) => {
    navigate("/shipment-gateway/shipment-details", {
      state: {
        pathname: location.pathname,
        containerID: record.containerID,
        activeTab,
        selectedContainerNum,
      },
    });
  };

  const handleSearchInputChange = (value) => {
    if (value) {
      setSearchInput(value);
      if (value !== searchInput) {
        setSearchLoading(true);
        ContainerService.searchContainer(value)
          .then((response) => {
            if (response.status === 200) {
              setSearchResults(response.data);
            }
          })
          .catch((err) => {
            console.error("Search Failed", err);
            setSearchResults([]);
          })
          .finally(() => {
            setSearchLoading(false);
          });
      } else {
        setSearchResults([]);
      }
    } else {
      setSearchInput("");
      setSearchResults([]);
    }
  };

  const handleContainerSelect = (selectedContainer) => {
    setSearchInput("");
    setSearchResults([]);
    const { containerNum } = selectedContainer;
    setIsSearchModalVisible(false);
    setSelectedContainerNum(containerNum);
    const updatedRequest = { ...list.request, containerNum };
    setList((prev) => ({
      ...prev,
      request: updatedRequest,
    }));
    fetchShipmentDetentionData(updatedRequest);
  };

  const handleCancelModal = () => {
    setSearchInput("");
    setSearchResults([]);
    setIsSearchModalVisible(false);
  };

  const handleClear = () => {
    setSearchInput("");
    setSearchResults([]);
  };

  const handleClearSearchResult = () => {
    setSelectedContainerNum(null);
    setSearchInput("");
    setSearchResults([]);
    setList((prev) => ({
      ...prev,
      request: {
        ...prev.request,
        containerNum: null,
      },
    }));
    fetchShipmentDetentionData({
      ...list.request,
      containerNum: null,
    });
  };

  const currentField = list.request.sortFilter.field;
  const currentOrder =
    list.request.sortFilter.order === "asc"
      ? "ascend"
      : list.request.sortFilter.order === "desc"
        ? "descend"
        : null;

  const columnsMap = {
    detentionMonitoring: [
      {
        ...leftColumn(
          <div className="ml-12">Container</div>,
          "containerNumber",
          (_, record) => (
            <div className="ml-8">
              <Button
                className="w-36 overflow-hidden whitespace-nowrap font-bold text-sm"
                type="primary"
                onClick={() => handleView(record)}
              >
                {record.containerNumber}
              </Button>
            </div>
          ),
          true,
          "w-36" // Explicit width to match Button
        ),
        sortOrder: currentField === "containerNumber" ? currentOrder : null,
      },
      {
        ...leftColumn(
          "Warehouse",
          "warehouse",
          (value) => <div className="w-36">{value}</div>,
          true,
          "w-36"
        ),
        sortOrder: currentField === "warehouse" ? currentOrder : null,
      },
      {
        ...dateColumn("ETA", "eta", "w-36"),
        sortOrder: currentField === "eta" ? currentOrder : null,
      },
      {
        ...dateColumn("Discharge Date", "dischargeDate", "w-36"),
        sortOrder: currentField === "dischargeDate" ? currentOrder : null,
      },
      {
        ...dateColumn("Delivery Date", "deliveryDate", "w-36"),
        sortOrder: currentField === "deliveryDate" ? currentOrder : null,
      },
      {
        ...dateColumn(
          <span className="whitespace-normal break-words">
            Estimated Detention Start
          </span>,
          "estimatedDetentionStart",
          "w-36"
        ),
        sortOrder:
          currentField === "estimatedDetentionStart" ? currentOrder : null,
      },
      {
        ...leftColumn(
          <span className="whitespace-normal break-words">
            Detention Free Days
          </span>,
          "detentionFreeDays",
          (value) => <div className="font-bold w-36">{value}</div>,
          true,
          "w-36"
        ),
        sortOrder: currentField === "detentionFreeDays" ? currentOrder : null,
      },
      {
        ...leftColumn(
          <span className="whitespace-normal break-words">
            Days To Detention Start
          </span>,
          "daysToDetentionStart",
          (value) => <div className="font-bold w-36">{value}</div>,
          true,
          "w-36"
        ),
        sortOrder:
          currentField === "daysToDetentionStart" ? currentOrder : null,
      },
      centerColumn(
        <div>Indicator</div>,
        "daysToDetentionStartIndicator",
        (_, record) => (
          <div className="flex items-center justify-center w-24">
            <span
              className={`w-4 h-4 opacity-60 rounded-full`}
              style={{
                backgroundColor: getDetentionColorClasses(
                  record.daysToDetentionStart
                ),
              }}
            />
          </div>
        ),
        false,
        "w-24"
      ),
      {
        ...dateColumn("Unload Complete", "unloadComplete", "w-36"),
        sortOrder: currentField === "unloadComplete" ? currentOrder : null,
      },
      {
        ...dateColumn("Collection Booked", "collectionBooked", "w-36"),
        sortOrder: currentField === "collectionBooked" ? currentOrder : null,
      },
      {
        ...leftColumn(
          "Container Type",
          "containerType",
          (value) => <div className="font-bold w-36">{value}</div>,
          true,
          "w-36"
        ),
        sortOrder: currentField === "containerType" ? currentOrder : null,
      },
      centerColumn(
        "Load Type",
        "containerLoadType",
        (value, record) => (
          <div className="flex items-center justify-center w-24">
            {record.containerLoadType === "LCL"
              ? record.containerLoadType
              : value}
          </div>
        ),
        false,
        "w-24"
      ),
      {
        ...leftColumn(
          "SeaLine",
          "seaLine",
          (value) => <div className="font-bold w-36">{value}</div>,
          true,
          "w-36"
        ),
        sortOrder: currentField === "seaLine" ? currentOrder : null,
      },
      leftColumn(
        "POL",
        "pol",
        (_, record) => (
          <div className="font-medium flex justify-start items-center w-24">
            {record.pol && (
              <ReactCountryFlag
                countryCode={record.pol.substring(0, 2)}
                svg
                className="!w-6 !h-4"
              />
            )}
            <span className="ml-2">
              {record.pol ? record.pol.substring(2) : ""}
            </span>
          </div>
        ),
        false,
        "w-24"
      ),
      leftColumn(
        "POD",
        "pod",
        (_, record) => (
          <div className="font-medium flex justify-start items-center w-24">
            {record.pod && (
              <ReactCountryFlag
                countryCode={record.pod.substring(0, 2)}
                svg
                className="!w-6 !h-4"
              />
            )}
            <span className="ml-2">
              {record.pod ? record.pod.substring(2) : ""}
            </span>
          </div>
        ),
        false,
        "w-24"
      ),
    ],
  };

  const fetchShipmentDetentionData = (request) => {
    setLoading(true);
    ShipmentService.fetchShipmentDetentionData(request)
      .then((response) => {
        if (response.status === 200) {
          setList({
            request,
            response: response.data,
          });
          setCounts(response.data.total);
        }
      })
      .catch((error) => {
        console.error("Failed to load data:", error);
      })
      .finally(() => setLoading(false));
  };

  const handleTableChange = (pagination = {}, filters, sorter) => {
    const updatedRequest = {
      ...list.request,
      sortFilter: {
        field: sorter.field || "",
        order:
          sorter.order === "ascend"
            ? "asc"
            : sorter.order === "descend"
              ? "desc"
              : "",
      },
      pageFilter: {
        ...list.request.pageFilter,
        size: pagination.pageSize || list.request.pageFilter.size,
        num: (pagination.current ?? list.request.pageFilter.num + 1) - 1,
      },
    };
    setList((prev) => ({ ...prev, request: updatedRequest }));
    fetchShipmentDetentionData(updatedRequest);
  };

  const renderTable = (viewKey) => {
    const columns = columnsMap[viewKey];
    const current = (list.request?.pageFilter?.num ?? 0) + 1;
    const pageSize = list.request?.pageFilter?.size ?? 50;
    const total = list.response?.total ?? 0;
    const totalPages = Math.ceil(total / pageSize);

    const currentSorter = {
      field: list.request.sortFilter.field,
      order:
        list.request.sortFilter.order === "asc"
          ? "ascend"
          : list.request.sortFilter.order === "desc"
            ? "descend"
            : "",
    };

    const onCustomPageChange = (page, size) => {
      handleTableChange({ current: page, pageSize: size }, {}, currentSorter); // Pass current sorter instead of {}.
    };

    const onPageSizeChange = (size) => {
      onCustomPageChange(1, size); // reset to first page when size changes
    };
    return (
      <div className="">
        <Spin spinning={loading}>
          <Table
            showSorterTooltip={false}
            columns={columns}
            dataSource={
              loading
                ? new Array(list.request.pageFilter.size)
                  .fill(null)
                  .map((_, index) => ({ key: index }))
                : list.response.data?.map((item, index) => ({
                  ...item,
                  key: index,
                }))
            }
            pagination={false}
            onChange={handleTableChange}
            scroll={{ x: "max-content", y: "calc(100vh - 270px)" }}
            size="small"
            sticky
            className="min-h-[calc(100vh - 300px)] h-[calc(100vh - 300px)]"
            rowClassName={(_, index) =>
              index % 2 === 0 ? "text-sm" : "text-sm bg-[#f3f3f3]"
            }
          />
          <div className="flex justify-between items-center mt-2">
            {/* Rows per page selector + info */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Rows per page:</span>
              <Select
                value={String(pageSize || 50)}
                onChange={(val) => onPageSizeChange(Number(val))}
                size="medium"
                style={{ width: 80 }}
              >
                <Option value="10">10</Option>
                <Option value="20">20</Option>
                <Option value="50">50</Option>
                <Option value="100">100</Option>
              </Select>
            </div>

            {/* Custom buttons */}
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">
                {`${(current - 1) * pageSize + 1}-${Math.min(
                  current * pageSize,
                  total
                )} of ${total} items`}
              </span>
              <div className="flex gap-2">
                <Button
                  size="medium"
                  onClick={() => onCustomPageChange(1, pageSize)}
                  disabled={current === 1}
                >
                  <FontAwesomeIcon icon={faAngleDoubleLeft} />
                </Button>
                <Button
                  size="medium"
                  onClick={() => onCustomPageChange(current - 1, pageSize)}
                  disabled={current === 1}
                >
                  <FontAwesomeIcon icon={faAngleLeft} />
                </Button>
                <Button
                  size="medium"
                  onClick={() => onCustomPageChange(current + 1, pageSize)}
                  disabled={current === totalPages}
                >
                  <FontAwesomeIcon icon={faAngleRight} />
                </Button>
                <Button
                  size="medium"
                  onClick={() => onCustomPageChange(totalPages, pageSize)}
                  disabled={current === totalPages}
                >
                  <FontAwesomeIcon icon={faAngleDoubleRight} />
                </Button>
              </div>
            </div>
          </div>
        </Spin>
      </div>
    );
  };

  return (
    <div className="px-2 relative  w-full h-full">
      <div className="flex justify-between items-center w-full my-4  px-2">
        <div className="flex justify-start items-center font-inter">
          <Title
            level={2}
            className="!font-medium !text-[#015373] !m-0 pl-4 mt-10"
          >
            Detention Tracker
          </Title>
        </div>

        <AutoComplete
          className="self-end border w-72 font-inter"
          placeholder="Search container"
          prefix={
            <FontAwesomeIcon
              icon={faMagnifyingGlass}
              className="opacity-70 mr-2"
            />
          }
          options={searchResults.map((item) => ({
            value: item.containerNum, // what shows in input when selected
            label: (
              <div className="p-2">
                <span className="font-bold">{item.containerNum}</span> -{" "}
                {item.tabName
                  .replace(/([A-Z])/g, " $1")
                  .replace(/^./, (str) => str.toUpperCase())}
              </div>
            ),
            item,
          }))}
          value={searchInput}
          onChange={handleSearchInputChange}
          onSelect={(value, option) => {
            const formattedTabName = option.item.tabName
              .replace(/([A-Z])/g, " $1") // split camelCase
              .replace(/^./, (str) => str.toUpperCase()); // capitalize first letter

            handleContainerSelect(option.item);

            // Input will show plain text (no bold possible)
            setSearchInput(`${option.item.containerNum} - ${formattedTabName}`);
          }}
          allowClear
          onClear={handleClearSearchResult}
          notFoundContent={
            searchLoading ? <Spin size="small" /> : "No results found"
          }
        />
      </div>
      <Card className="rounded-3xl shadow-xl bg-white bg-opacity-80">
        <Tabs
          type="card"
          tabBarGutter={8}
          activeKey={activeTab}
          onChange={(activeKey) => setActiveTab(activeKey)}
          items={tabs.map((key) => ({
            key,
            label: (
              <>
                <span className="text-[#015373] font-medium">
                  {key
                    .replace(/([A-Z])/g, " $1")
                    .replace(/^./, (str) => str.toUpperCase())}
                </span>
                <span className="px-2 ml-2 rounded-md bg-[#01A4A6] font-semibold text-white">
                  {counts}
                </span>
              </>
            ),
            children: renderTable(key),
          }))}
        />
        <Modal
          title="Search Container"
          open={isSearchModalVisible}
          onCancel={handleCancelModal}
          footer={null}
          destroyOnClose
        >
          <Input
            placeholder="Enter container number"
            value={searchInput}
            onChange={handleSearchInputChange}
            onClear={handleClear}
            allowClear
          />
          <Spin spinning={searchLoading}>
            <div className="mt-2 max-h-96 overflow-auto">
              {searchResults.length > 0 ? (
                searchResults.map((item, index) => (
                  <div
                    key={index}
                    className="p-2 hover:bg-gray-200 cursor-pointer border-b"
                    onClick={() => handleContainerSelect(item)}
                  >
                    <div>
                      <span className="font-bold">{item.containerNum}</span> -{" "}
                      {item.tabName
                        .replace(/([A-Z])/g, " $1")
                        .replace(/^./, (str) => str.toUpperCase())}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-gray-400 mt-4 text-center">
                  No results found
                </div>
              )}
            </div>
          </Spin>
        </Modal>
      </Card>
    </div>
  );
};

export default ShipmentDetentionPage;
